import {PageNumberRenderer} from './renderers';
import {frameBorderFor} from './LivePreviewParts';
import {pageNumberBand, pageNumberJustify, stampsOnPage} from '../lib/page-numbers';
import type {PageNumberStamp} from '../lib/page-numbers';

/** The stamp's band. Above the group-sentinel click catcher (5) and below page
 *  content (10): a stamp lives in the page margin, so it never has to win against
 *  content — it only has to stay clickable. */
const PAGE_NUMBER_Z = 6;

/**
 * The page-number stamps that draw on ONE page of the preview.
 *
 * Which stamps those are, and what each shows, is `stampsOnPage`'s call — this
 * draws the result. Each one occupies a FIXED band (the page's own top or bottom
 * margin, spanning the content width), so there is nothing to drag or resize:
 * `placement` and `align` are the whole of its position. The band is
 * click-to-select in the editor and inert in fill mode, where it is printed output.
 */
export function PageNumberStamps({
  stamps,
  pageIdx,
  totalPages,
  margins,
  canInteract,
  selectedIds,
  hoveredId,
  registerRef,
  onHoverChange,
  onSelect,
}: {
  stamps: readonly PageNumberStamp[];
  pageIdx: number;
  totalPages: number;
  margins: {top: number; bottom: number; left: number; right: number};
  canInteract: boolean;
  selectedIds?: string[];
  hoveredId: string | null;
  registerRef: (id: string, el: HTMLDivElement | null) => void;
  onHoverChange: (id: string | null) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      {stampsOnPage(stamps, pageIdx, totalPages, {includePlaceholder: canInteract}).map(
        ({item, pn, text, isOrigin}) => {
          // Same frame rule as the absolute-item path: chrome only while the author
          // is pointing at the band or has it selected, never in fill mode.
          const isSelected = selectedIds?.includes(item.id) ?? false;
          const hovered = hoveredId === item.id;
          const showFrame = canInteract && (hovered || isSelected);
          return (
            <div
              key={item.id}
              ref={isOrigin ? (el) => registerRef(item.id, el) : undefined}
              onMouseEnter={() => canInteract && onHoverChange(item.id)}
              onMouseLeave={() => canInteract && onHoverChange(null)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                if (!canInteract) return;
                e.stopPropagation();
                onSelect(item.id);
              }}
              className="absolute flex items-center"
              style={{
                ...pageNumberBand(pn, margins),
                justifyContent: pageNumberJustify(pn.align),
                border: showFrame
                  ? frameBorderFor({
                      inGroupSelectionMode: false,
                      active: false,
                      isSelected,
                      hovered,
                    })
                  : undefined,
                boxSizing: 'border-box',
                cursor: canInteract ? 'pointer' : 'default',
                pointerEvents: canInteract ? 'auto' : 'none',
                userSelect: 'none',
                zIndex: PAGE_NUMBER_Z,
              }}
            >
              <PageNumberRenderer item={item} pn={pn} text={text} />
            </div>
          );
        },
      )}
    </>
  );
}
